import { useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminPermissionGate } from '../../components/primitives/admin-permission-gate.js';
import { AdminApiError } from '../../api/admin-client.js';
import { useAdminWarehouses, useAdminWarehouseMutations } from '../hooks/use-inventory.js';
import { WarehouseForm } from '../components/warehouse-form.js';
import type { AdminWarehouse } from '../types/inventory.js';

/** Create/edit/deactivate are ADMIN-only (Part 2 "manage warehouses") -- STAFF can still view this page (the list query itself has no role restriction beyond the shared admin-shell gate), the mutation controls are simply hidden via AdminPermissionGate for a STAFF viewer, matching what the backend independently enforces regardless. */
export function WarehousesPage() {
  const { data, isLoading, isError, refetch } = useAdminWarehouses();
  const { create, update } = useAdminWarehouseMutations();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AdminWarehouse | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminWarehouse>[] = [
    { key: 'code', header: 'Code', render: (row) => row.code },
    { key: 'name', header: 'Name', render: (row) => row.name },
    { key: 'city', header: 'Location', render: (row) => [row.city, row.state].filter(Boolean).join(', ') || '—' },
    {
      key: 'isDefault',
      header: 'Default',
      render: (row) => (row.isDefault ? <AdminStatusBadge label="Default" tone="info" /> : '—'),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => (
        <AdminStatusBadge label={row.isActive ? 'Active' : 'Inactive'} tone={row.isActive ? 'success' : 'neutral'} />
      ),
    },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Warehouses"
        description="Manage fulfillment locations."
        breadcrumbs={[{ label: 'Warehouses' }]}
        actions={
          <AdminPermissionGate anyRole={['ADMIN']}>
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="bg-brand text-brand-foreground inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New warehouse
            </button>
          </AdminPermissionGate>
        }
      />

      {actionError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      <AdminSectionCard title="All warehouses" className="p-4 sm:p-5">
        {showCreate && (
          <div className="mb-4">
            <WarehouseForm
              isSubmitting={create.isPending}
              onCancel={() => setShowCreate(false)}
              onSubmit={(input) =>
                create.mutate(input, {
                  onSuccess: () => setShowCreate(false),
                  onError: (error) =>
                    setActionError(error instanceof AdminApiError ? error.message : 'Could not create warehouse.'),
                })
              }
            />
          </div>
        )}
        {editing && (
          <div className="mb-4">
            <WarehouseForm
              initial={editing}
              isSubmitting={update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                update.mutate(
                  { id: editing.id, input },
                  {
                    onSuccess: () => setEditing(null),
                    onError: (error) =>
                      setActionError(error instanceof AdminApiError ? error.message : 'Could not update warehouse.'),
                  },
                )
              }
            />
          </div>
        )}

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No warehouses yet"
          rowActions={(row) => (
            <AdminPermissionGate anyRole={['ADMIN']}>
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Edit
                </button>
                {row.isActive ? (
                  <button
                    type="button"
                    onClick={() =>
                      update.mutate(
                        { id: row.id, input: { isActive: false } },
                        {
                          onError: (error) =>
                            setActionError(
                              error instanceof AdminApiError ? error.message : 'Could not deactivate warehouse.',
                            ),
                        },
                      )
                    }
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: row.id, input: { isActive: true } })}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Activate
                  </button>
                )}
              </div>
            </AdminPermissionGate>
          )}
        />
      </AdminSectionCard>
    </AdminContent>
  );
}
